// 📄 pages/Proyectos/TransaccionesCatalogo.tsx
// Consulta (solo lectura) del Catálogo de Transacciones SAP dentro de un
// proyecto — vive en /proyecto/:projectId/transacciones, dentro del grupo
// "Herramientas" del sidebar. El catálogo es global (no por proyecto, ver
// catalogoTransaccionesService.ts): el CRUD completo vive en /admin >
// Transacciones; acá cualquier consultor solo lo consulta/filtra/copia
// mientras trabaja en su integración. Mismo patrón que [[BadiCatalogo.tsx]].
import { useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Terminal, Info } from 'lucide-react';
import { TransaccionSap, getTransacciones, seedCatalogoTransacciones } from '../../services/catalogoTransaccionesService.ts';
import { TransaccionZ, getTransaccionesZ } from '../../services/transaccionZService.ts';
import TransactionCatalogGrid from '../../components/abap/TransactionCatalogGrid.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { useProject } from '../../context/ProjectContext.tsx';

type VistaTransacciones = 'todas' | 'estandar' | 'z';

export default function TransaccionesCatalogo() {
  const { profile } = useAuth();
  const esAdmin = profile?.role === 'admin';
  const { projectId } = useProject();
  const [vista, setVista] = useState<VistaTransacciones>('todas');
  const [transacciones, setTransacciones] = useState<TransaccionSap[]>([]);
  const [loading, setLoading] = useState(true);
  const [sembrando, setSembrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Solo un Admin tiene permiso de escritura en catalogoTransacciones (ver
  // firestore.rules) — un consultor común que entre acá con el catálogo
  // vacío NUNCA debe intentar sembrarlo (fallaría con permission-denied).
  const seedIntentado = useRef(false);

  // Desarrollos Z de este proyecto — se cargan aparte (colección/servicio
  // distintos, ver ConfiguracionTransaccionesZ.tsx donde se administran).
  const [transaccionesZ, setTransaccionesZ] = useState<TransaccionZ[]>([]);
  const [loadingZ, setLoadingZ] = useState(true);
  const [errorZ, setErrorZ] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        let data = await getTransacciones();
        if (data.length === 0 && esAdmin && !seedIntentado.current) {
          seedIntentado.current = true;
          setSembrando(true);
          try {
            await seedCatalogoTransacciones();
            data = await getTransacciones();
          } finally {
            setSembrando(false);
          }
        }
        setTransacciones(data);
      } catch (err) {
        console.error('Error cargando el catálogo de transacciones:', err);
        setError('No se pudo cargar el catálogo de transacciones.');
      } finally {
        setLoading(false);
      }
    })();
  }, [esAdmin]);

  useEffect(() => {
    if (!projectId) { setLoadingZ(false); return; }
    setLoadingZ(true);
    getTransaccionesZ(projectId)
      .then(setTransaccionesZ)
      .catch((err) => {
        console.error('Error cargando las transacciones Z del proyecto:', err);
        setErrorZ('No se pudieron cargar las transacciones Z de este proyecto.');
      })
      .finally(() => setLoadingZ(false));
  }, [projectId]);

  // El grid espera `transaccion` como nombre del código (ver
  // TransactionCatalogGrid.tsx) — el modelo Z usa `codigo` a propósito
  // (distinto del catálogo global), así que se adapta acá antes de pasarlo.
  const transaccionesZAdaptadas = useMemo(
    () => transaccionesZ.map((z) => ({
      id: z.id,
      transaccion: z.codigo,
      nombre: z.nombre,
      categoria: z.categoria,
      solucion: z.solucion,
      descripcion: z.descripcion,
      responsable: z.responsable,
      sistemasDisponibles: z.sistemasDisponibles,
      rolPermiso: z.rolPermiso
    })),
    [transaccionesZ]
  );

  // El contador siempre refleja el total de Z del proyecto, sin importar el
  // filtro que esté activo DENTRO del grid de Z (ese filtro es estado local
  // de TransactionCatalogGrid, no llega hasta acá — ni falta que hace, el
  // pedido es mostrar el total, no lo filtrado).
  const zCount = transaccionesZ.length;

  const OPCIONES_VISTA: { id: VistaTransacciones; label: string }[] = [
    { id: 'todas', label: 'Todas' },
    { id: 'estandar', label: 'Estándar' },
    { id: 'z', label: zCount > 0 ? `Desarrollos Z (${zCount})` : 'Desarrollos Z' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {OPCIONES_VISTA.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setVista(id)}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all ${
              vista === id
                ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {vista !== 'z' && (
        <>
          <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Catálogo global de referencia técnica, mantenido desde el panel de Administración. Filtra por Categoría o Solución y usa el botón de copiar para llevarte el código de transacción directo al portapapeles.</span>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
          )}

          {loading ? (
            <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
              <p className="text-sm">{sembrando ? 'Inicializando catálogo con datos de ejemplo...' : 'Cargando catálogo...'}</p>
            </div>
          ) : transacciones.length === 0 ? (
            <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
              <Terminal className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm">Todavía no hay transacciones registradas en el catálogo. Un Admin puede cargarlas desde /admin.</p>
            </div>
          ) : (
            <TransactionCatalogGrid transacciones={transacciones} readOnly />
          )}
        </>
      )}

      {vista !== 'estandar' && projectId && (
        <div className={vista === 'todas' ? 'pt-6 border-t border-white/10 space-y-4' : 'space-y-4'}>
          <div>
            <h3 className="text-sm font-bold text-white">Desarrollos Z de este proyecto</h3>
            <p className="text-xs text-gray-500 mt-0.5">Transacciones personalizadas configuradas para este proyecto. Solo tú y tu equipo las ven.</p>
          </div>

          {errorZ && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{errorZ}</div>
          )}

          {loadingZ ? (
            <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
              <p className="text-sm">Cargando transacciones Z...</p>
            </div>
          ) : transaccionesZ.length === 0 ? (
            <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
              <Terminal className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm">Aún no hay transacciones Z en este proyecto. Agrégalas en Configuración &gt; Desarrollo Z.</p>
            </div>
          ) : (
            <TransactionCatalogGrid transacciones={transaccionesZAdaptadas} esZ readOnly />
          )}
        </div>
      )}
    </div>
  );
}
